/* External */
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';

/* Assets */
import genericCard16X9 from '@/assets/theme/images/16x9-GenericCard.png';

/* Components */
import LiveTag from '../liveTag/LiveTag';
import PdpButtons from '../pdpButtons/PdpButtons';
import SkeletonBox from '../skeletonBox/SkeletonBox';

/* Styles */
import './NewDetailsFrame.scss';

/* Other */
import {
  GRADIENTS, useImage, getBadgeLabelTitle,
} from '../../utils';
import { getImage } from '../../theme/images';
import { useIsResponsive } from '../../hooks/useIsResponsive';

function NewDetailsFrame({
  assetId,
  assetToPlay: {
    images, type, live, vrioAssetId, vod, rating, shouldDisplayLockIcon, actionOrigin, vmsId,
  } = {},
  description,
  fullHeight,
  isContinueWatchingAsset,
  isLive,
  isLoading,
  genreMetadata,
  durationMetadata,
  pdpPlayButtonText,
  pdpPlayButtonIcon,
  progress,
  showMyListButton,
  subtitle,
  title,
  bannerBottomRight,
  isPublic,
}) {
  const genericCard16x9Flag = getImage('16x9GenericCard') || genericCard16X9;
  const isMobile = useIsResponsive();
  const isTablet = useIsResponsive('(min-width: 501px) and (max-width: 1024px)');

  const descriptonRef = useRef(null);
  const [needsReadMore, setNeedsReadMore] = useState(false);
  const [isDescriptionCollapsed, setIsDescriptionCollapsed] = useState(false);

  useEffect(() => {
    if (descriptonRef.current && isMobile) {
      const lineHeight = Number.parseFloat(
        getComputedStyle(descriptonRef.current).lineHeight,
      );
      const maxHeight = lineHeight * 3;
      if (descriptonRef.current.scrollHeight > maxHeight) {
        setNeedsReadMore(false);
        setIsDescriptionCollapsed(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [description, isMobile, descriptonRef.current]);

  let backgroundImageType;

  if (isMobile || isTablet) {
    backgroundImageType = 'RECTANGLE_VERTICAL';
  } else if (!isPublic) {
    backgroundImageType = '16:9';
  } else {
    backgroundImageType = 'BACKGROUND';
  }

  const pdpGradient = isMobile ? GRADIENTS.PDP_MOBILE : GRADIENTS.PDP;

  const { imageRef, imageStyle } = useImage(
    images,
    genericCard16x9Flag,
    pdpGradient,
    backgroundImageType,
    isLoading,
  );
  const assetToPlayId = vrioAssetId;
  const { channelId, startTime, endTime } = live || {};
  const { duration } = vod || {};
  const { country } = useParams() || {};

  const badgeLabel = getBadgeLabelTitle(type);

  return (
    <div
      className={classNames('dtv-new-details-frame', {
        'full-height': fullHeight,
        loading: isLoading,
      })}
      ref={imageRef}
      style={imageStyle}
    >
      {isLoading ? (
        <>
          <SkeletonBox />
          <SkeletonBox />
          <SkeletonBox />
          <SkeletonBox />
          <SkeletonBox />
          <SkeletonBox />
        </>
      ) : (
        <>
          <div className="left-content">
            <div className="dtv-new-details-frame-content">
              {isLive && startTime && endTime && (
                <LiveTag
                  classText="dtv-new-details-frame-content-live-tag-text"
                  endTime={endTime}
                  startTime={startTime}
                />
              )}
              {badgeLabel ? (
                <div className="dtv-new-details-frame-content-badge">
                  <FormattedMessage id={badgeLabel} />
                </div>
              ) : (
                <div className="dtv-new-details-frame-content-badge__loading">
                  <SkeletonBox />
                </div>
              )}
              {title && (
                <h1
                  className={classNames({
                    'dtv-new-details-frame-content-title': true,
                  })}
                >
                  {title}
                </h1>
              )}
              {subtitle && <h2 className="dtv-new-details-frame-content-subtitle">{subtitle}</h2>}
              {genreMetadata ? <span className="dtv-new-details-frame-content-metadata">{genreMetadata}</span> : (
                <div className="dtv-new-details-frame-content-metadata__loading">
                  <SkeletonBox />
                </div>
              )}
              {description ? (
                <div className="dtv-new-details-frame-content-description">
                  <p
                    ref={descriptonRef}
                    className={classNames('dtv-new-details-frame-content-description-label', {
                      'dtv-new-details-frame-content-description__collapse': isDescriptionCollapsed && isMobile,
                    })}
                  >
                    {description}
                  </p>
                  {isMobile && needsReadMore && (
                    <button
                      type="button"
                      className="dtv-new-details-frame-content-description-readmore"
                      onClick={() => setIsDescriptionCollapsed((prevCollapse) => !prevCollapse)}
                    >
                      {isDescriptionCollapsed ? <FormattedMessage id="common.readMore" /> : <FormattedMessage id="common.readLess" />}
                    </button>
                  )}
                </div>
              ) : (
                <div className="dtv-new-details-frame-content-description__loading"><SkeletonBox /></div>
              )}
              {!isLive && (
                <div>
                  {durationMetadata ? <span className="dtv-new-details-frame-content-metadata">{durationMetadata}</span> : (
                    <div className="dtv-new-details-frame-content-metadata__loading">
                      <SkeletonBox />
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="dtv-new-details-frame-actions">
              {assetToPlayId && (
                <PdpButtons
                  assetId={assetId}
                  vmsId={vmsId}
                  assetToPlayId={assetToPlayId}
                  assetType={type}
                  actionOrigin={actionOrigin}
                  country={country}
                  pdpPlayButtonIcon={pdpPlayButtonIcon}
                  pdpPlayButtonText={pdpPlayButtonText}
                  shouldDisplayLockIcon={shouldDisplayLockIcon}
                  isPublic={isPublic}
                  showMyListButton={showMyListButton}
                  title={title}
                  channelId={channelId}
                  rating={rating}
                  isLive={isLive}
                  duration={duration}
                  progress={isContinueWatchingAsset ? progress : 0}
                />
              )}

              {!assetToPlayId && (
                <div className="dtv-new-details-frame-actions__loading">
                  <SkeletonBox />
                </div>
              )}
            </div>
          </div>
          <div className="right-content">
            {bannerBottomRight}
          </div>
        </>
      )}
    </div>
  );
}

NewDetailsFrame.propTypes = {
  assetId: PropTypes.string,
  assetToPlay: PropTypes.shape({
    vrioAssetId: PropTypes.string,
    duration: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    progress: PropTypes.number,
    type: PropTypes.string,
    channelId: PropTypes.string,
    shouldDisplayLockIcon: PropTypes.bool,
  }),
  bannerBottomRight: PropTypes.node,
  description: PropTypes.node,
  fullHeight: PropTypes.bool,
  isContinueWatchingAsset: PropTypes.bool,
  isLive: PropTypes.bool,
  isLoading: PropTypes.bool,
  durationMetadata: PropTypes.node,
  genreMetadata: PropTypes.node,
  pdpPlayButtonText: PropTypes.string,
  showMyListButton: PropTypes.bool,
  subtitle: PropTypes.node,
  title: PropTypes.node,
  isPublic: PropTypes.bool,
};

NewDetailsFrame.defaultProps = {
  assetId: '',
  assetToPlay: {},
  bannerBottomRight: null,
  description: '',
  fullHeight: false,
  isContinueWatchingAsset: false,
  isLive: false,
  isLoading: false,
  durationMetadata: '',
  genreMetadata: '',
  pdpPlayButtonText: '',
  showMyListButton: false,
  subtitle: '',
  title: '',
  isPublic: false,
};

export default injectIntl(NewDetailsFrame);
